import React, { useRef, useEffect, useState } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { gsap } from "gsap";
import ShimmerButton from "../animations/ShimmerButton";

export const HeroSection = () => {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const taglineRef = useRef(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });

  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  const titleOpacity = useTransform(smoothProgress, [0, 0.3], [1, 0]);
  const titleY = useTransform(smoothProgress, [0, 0.3], [0, -100]);
  const contentOpacity = useTransform(smoothProgress, [0.2, 0.5], [0, 1]);
  const contentY = useTransform(smoothProgress, [0.2, 0.5], [50, 0]);

  useEffect(() => {
    if (taglineRef.current) {
      gsap.fromTo(
        taglineRef.current,
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 1, delay: 0.5, ease: "power3.out" }
      );
    }
  }, []);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = true;
      videoRef.current.defaultMuted = true;
      videoRef.current.play().catch(e => console.log('Video autoplay prevented:', e));
    }
  }, []);

  const scrollToContact = () => {
    const contactSection = document.querySelector("#contact");
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      id="home"
      ref={containerRef}
      className="relative min-h-[150vh]"
      data-testid="hero-section"
    >
      {/* Video Background - 100% visible */}
      <div className="fixed inset-0 z-0 overflow-hidden">
        {/* Gradient fallback while video loads */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(135deg, #FFE5CC 0%, #FFD4C4 25%, #F5C6C6 50%, #E8C4D4 75%, #D4C4E8 100%)'
          }}
        />
        
        <video
          ref={videoRef}
          autoPlay
          muted
          defaultMuted
          loop
          playsInline
          controls={false}
          disablePictureInPicture
          controlsList="nodownload noplaybackrate noremoteplayback nofullscreen"
          preload="auto"
          onLoadedData={() => setVideoLoaded(true)}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${videoLoaded ? 'opacity-100' : 'opacity-0'}`}
        >
          <source src="/hero-video.mp4" type="video/mp4" />
        </video>
      </div>

      {/* Sticky container */}
      <div className="sticky top-0 h-screen flex items-center justify-center overflow-hidden z-10">
        {/* Title overlay - fades out on scroll */}
        <motion.div
          style={{ opacity: titleOpacity, y: titleY }}
          className="absolute inset-0 flex flex-col items-center justify-center text-center z-20 px-4 pointer-events-none"
        >
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="text-warm-brown/80 uppercase tracking-[0.2em] text-sm font-semibold mb-6"
            style={{ textShadow: "0 2px 12px rgba(255,255,255,0.95)" }}
          >
            Licensed Clinical Psychologist
          </motion.p>
          
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="font-serif text-4xl sm:text-5xl lg:text-7xl text-warm-brown max-w-4xl leading-tight"
            style={{ textShadow: "0 2px 20px rgba(255,255,255,1)" }}
          >
            Your Mind Deserves Care
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.6 }}
            className="mt-12 text-warm-brown animate-bounce text-sm"
            style={{ textShadow: "0 2px 10px rgba(255,255,255,0.9)" }}
          >
            Scroll to Begin Your Journey ↓
          </motion.p>
        </motion.div>

        {/* Content that fades in on scroll */}
        <motion.div
          style={{ opacity: contentOpacity, y: contentY }}
          className="relative z-30 text-center px-4 max-w-2xl bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl"
        >
          <h2
            ref={taglineRef}
            className="font-serif text-2xl sm:text-3xl lg:text-4xl text-warm-brown mb-4"
          >
            From Surviving to Thriving — One Conversation at a Time
          </h2>
          <p className="font-sans text-warm-brown font-medium mb-8 text-sm sm:text-base">
            Priya Ahuja, M.Phil Clinical Psychology | Licensed Psychologist | Jaipur
          </p>
          <ShimmerButton
            onClick={scrollToContact}
            data-testid="hero-cta-button"
          >
            Book a Free Consultation
          </ShimmerButton>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
